import { existsSync, readFileSync, writeFileSync, chmodSync, unlinkSync } from 'node:fs'
import { join } from 'node:path'

export const HOOK_MARKER = '# gsd-munch: auto re-index on commit'

/**
 * Installs a post-commit hook in the given git repo that re-indexes changed files
 * using the jCodeMunch venv Python directly (bypassing MCP protocol).
 *
 * @param repoRoot     Absolute path to the git repository root
 * @param venvPythonPath  Absolute path to the venv Python binary
 * @param pkgPythonDir    Absolute path to the package python/ dir (PYTHONPATH)
 * @returns 'installed'      — hook was created/appended successfully
 *          'already_present' — hook already has the gsd-munch marker (idempotent)
 *          'skipped'         — .git/hooks/ directory not found (not a git repo)
 */
export function installPostCommitHook(
  repoRoot: string,
  venvPythonPath: string,
  pkgPythonDir: string,
): 'installed' | 'already_present' | 'skipped' {
  const gitHooksDir = join(repoRoot, '.git', 'hooks')
  if (!existsSync(gitHooksDir)) {
    return 'skipped'
  }

  const hookPath = join(gitHooksDir, 'post-commit')
  const hookScript = buildHookScript(venvPythonPath, pkgPythonDir)

  if (existsSync(hookPath)) {
    const existing = readFileSync(hookPath, 'utf8')
    if (existing.includes(HOOK_MARKER)) {
      return 'already_present'
    }
    writeFileSync(hookPath, existing.trimEnd() + '\n\n' + hookScript + '\n', 'utf8')
  } else {
    writeFileSync(hookPath, '#!/usr/bin/env bash\n\n' + hookScript + '\n', 'utf8')
  }
  chmodSync(hookPath, 0o755)
  return 'installed'
}

/**
 * Removes the gsd-munch post-commit hook block from a git repo's post-commit hook.
 * If the hook file only contained the gsd-munch block (plus optional shebang), deletes the file.
 *
 * @param repoRoot  Absolute path to the git repository root
 * @returns true if the hook block was found and removed, false otherwise
 */
export function removePostCommitHook(repoRoot: string): boolean {
  const hookPath = join(repoRoot, '.git', 'hooks', 'post-commit')
  if (!existsSync(hookPath)) {
    return false
  }

  const content = readFileSync(hookPath, 'utf8')
  if (!content.includes(HOOK_MARKER)) {
    return false
  }

  // Remove from HOOK_MARKER to the next blank line after the closing `)`
  // The block ends with `) &` followed by a newline, so strip from marker to end of block
  const markerIndex = content.indexOf(HOOK_MARKER)
  const beforeMarker = content.slice(0, markerIndex).trimEnd()

  // Find the end of the block: look for `) &` after the marker position
  const afterMarker = content.slice(markerIndex)
  // The block closes with ) &\n — find end of that line
  const blockEndMatch = afterMarker.match(/\) &\n?/)
  let afterBlock = ''
  if (blockEndMatch && blockEndMatch.index !== undefined) {
    afterBlock = afterMarker.slice(blockEndMatch.index + blockEndMatch[0].length).trimStart()
  }

  const cleaned = beforeMarker + (afterBlock ? '\n\n' + afterBlock : '')

  // If remaining content is only the shebang line or empty, delete the file
  const trimmed = cleaned.trim()
  if (trimmed === '' || trimmed === '#!/usr/bin/env bash') {
    unlinkSync(hookPath)
  } else {
    writeFileSync(hookPath, cleaned + '\n', 'utf8')
  }

  return true
}

/**
 * Builds the post-commit hook shell script block with absolute paths embedded at install time.
 * The entire body runs in a background subshell and swallows all errors.
 */
function buildHookScript(venvPython: string, pkgPythonDir: string): string {
  return `${HOOK_MARKER}
(
  if [ -x "${venvPython}" ]; then
    CHANGED_DIRS=$(git diff-tree --no-commit-id -r --name-only HEAD 2>/dev/null \\
      | xargs -I{} dirname "{}" 2>/dev/null | sort -u | head -20)
    for DIR in $CHANGED_DIRS; do
      ABS_DIR=$(cd "$DIR" 2>/dev/null && pwd || true)
      if [ -n "$ABS_DIR" ]; then
        "${venvPython}" -c "
import sys
sys.path.insert(0, '${pkgPythonDir}')
from jcodemunch_mcp.tools.index_folder import index_folder
try:
    index_folder('$ABS_DIR')
except Exception as e:
    import os
    log = os.path.expanduser('~/.code-index/reindex.log')
    os.makedirs(os.path.dirname(log), exist_ok=True)
    open(log, 'a').write(str(e) + '\\n')
" 2>/dev/null || true
      fi
    done
  fi
) &`
}
